<template>
  <NuxtLayout>
    <div class="offerDetail">
      <HeroHeader class="offersHeader" :title-visible="false" stacked-elements>
        <div class="heroHeader__StackedElementsContent container">
          <div class="offersHeader__Texts">
            <h1 v-text="page.title" />
    
            <div class="offersHeader__Accommodation">
              <template v-if="accommodation">
                <h2 v-text="accommodation.name" />
                <div class="offerDetail__StarsWrapper">
                  <Icon name="star" v-for="i in accommodation.category_rating" :key="i" />
                </div>
              </template>
              <template v-else>
                <h2 v-text="$t('offerDetail.moreAccommodations')" />
              </template>
            </div>
          </div>
  
          <div v-if="page.image_document_discount?.meta?.format === 'svg' && page.image_document_discount.meta.url" class="offersHeader__DiscountWrapper">
            <img :src="page.image_document_discount.meta.url" :alt="page.image_document_discount.title || ''" width="300" height="200" />
          </div>
        </div>
      </HeroHeader>
      
      <div class="offerDetail__Content">
        <aside v-if="accommodation" class="offerDetail__Aside">
          <h3 v-text="accommodation.name" />

          <Button v-if="accommodationsDates" @click="openCalendar" class="button--Primary">{{ $t('bookNow') }}</Button>
        </aside>
  
        <CMSField v-if="page?.content" :data="page.content" />
      </div>

      <ReservationPanel sticky specialOffers ref="reservationPanel" />
    </div>
  </NuxtLayout>
</template>

<script setup>
  import { isEqual, isAfter, isBefore, startOfDay, endOfDay } from 'date-fns';

  defineOptions({
    auth: 'aminess'
  });
  
  const props = defineProps({
    page: {
      type: Object
    }
  });

  useHead({
    htmlAttrs: {
      class: 'fixedHeader fixedHeader--WithoutTopPadding',
    },
  });

  const { lang } = useLanguage();
  const { booking } = useBooking();
  const { accommodationsData, link } = useSetup();
  const { getAvailabilityDates, updateSearchDates } = useAvailabilityDates();
  const { openPanel } = useReservationPanel();
  const accommodationsDates = ref(null);

  let { book_date_from: dateFrom, book_date_to: dateTo } = props.page;
  try {
    if (dateFrom && dateTo) {
      const today = new Date();
      const activeFrom = startOfDay(dateFrom);
      const activeTo = endOfDay(dateTo);
      const isOfferActive = (isAfter(today, activeFrom) || isEqual(today, activeFrom)) && (isBefore(today, activeTo) || isEqual(today, activeTo));

      if (!isOfferActive) {
        const redirectPath = `/${ props.page.sef.split('/').filter(i => i).slice(0, -1).join('/') }/` || link('OfferList');
        await navigateTo({ path: redirectPath, query: { offer_expired: 1 } }, { redirectCode: 301 });
      }
    }
  } catch(error) {
    console.log(error)
  }

  const accommodation = computed(() => {
    if (props.page.accommodations?.length === 1) {
      let result = accommodationsData.value[props.page.accommodations[0]];
      const brandClass = useCamelCase(result.brand.theme_class);
      if (brandClass && document) {
        document.documentElement.style.setProperty('--themeColor', `var(--${brandClass})`);
        document.documentElement.style.setProperty('--themeColorDarken', `var(--${brandClass}Darken)`);
      }
      return result;
    }
    return '';
  });

  function openCalendar() {
    openPanel(1);
  }

  function getUnitListingLink() {
    updateSearchDates(accommodation.value.availability_calendar);
    let fullLink = { path: link('AccommodationUnitList'), query: { accommodation_id: accommodation.value.id } }
    let dates = getAvailabilityDates(accommodation.value.availability_calendar);
    if (props.page.package_external_id) fullLink.query.package_external_id = props.page.package_external_id;
    if (booking.value.promo_code) {
      fullLink.query.promo_code = booking.value.promo_code;
    }
    fullLink.query.date_from = dates.from;
    fullLink.query.date_to = dates.to;
    fullLink.query.adults_count = booking.value.adults_count;
    fullLink.query.children = booking.value.children;
    return navigateTo(fullLink);
  }

  onMounted(() => {
    if (accommodation.value?.availability_calendar?.length) {
      accommodationsDates.value = getAvailabilityDates(accommodation.value.availability_calendar);
    }
  });
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_offers.scss');
  @import url('@/assets/styles/pages/_offerDetail.scss');
</style>